import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { site } from '@shared/site.config';
import { LOCAL_PAGES } from '@/lib/localPages';
import BrandMark from '@/components/BrandMark';
import DimensionLine from '@/components/DimensionLine';

/** One cell of the title block. */
function Cell({ label, children, wide = false }: { label: string; children: ReactNode; wide?: boolean }) {
  return (
    <div className={`bg-sheet px-5 py-4 ${wide ? 'sm:col-span-2' : ''}`}>
      <p className="annot">{label}</p>
      <div className="mt-1.5 font-display text-[15px] leading-snug text-graphite">{children}</div>
    </div>
  );
}

export default function Footer() {
  const year = new Date().getFullYear();

  return (
    <footer>
      {/* Closing offer, on cyanotype. Every page ends on the same live offer
          rather than a link grid. */}
      <div className="relative bg-blueprint bg-grid-blueprint text-white">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-14">
          <DimensionLine label="Next step" inverse className="mb-8" />
          <div className="flex flex-wrap items-end justify-between gap-8">
            <div>
              <p className="font-display text-[30px] sm:text-[38px] leading-[1.08] max-w-lg">
                Ready to talk it through?
              </p>
              <p className="mt-2.5 font-mono text-[11px] uppercase tracking-wider2 text-blueprint-100/70">
                No form required — call or text directly
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <a href={`tel:${site.agent.phone.replace(/\D/g, '')}`} className="btn-primary">
                Call {site.agent.phone}
              </a>
              <Link to="/contact" className="btn-ghost-inverse">
                Send a message
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-medline bg-table">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-14">
          <div className="grid gap-10 md:grid-cols-[1.3fr_1fr_1fr_1fr]">
            <div>
              <div className="flex items-center gap-3">
                <BrandMark className="h-9 w-9" />
                <span className="font-display text-[19px] font-semibold leading-none">{site.brand.name}</span>
              </div>
              <p className="mt-4 max-w-xs text-[14px] leading-relaxed text-annot">{site.brand.description}</p>
            </div>

            <div>
              <p className="annot">Sheet index</p>
              <ul className="mt-4 space-y-2.5">
                {site.services.map((s, i) => (
                  <li key={s.slug}>
                    <Link
                      to={`/${s.slug}`}
                      className="group flex items-baseline gap-3 text-[14px] text-annot hover:text-graphite"
                    >
                      <span className="sheet-no group-hover:text-redline">A-0{i + 1}</span>
                      {s.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <p className="annot">Local sheets</p>
              <ul className="mt-4 space-y-2.5">
                {LOCAL_PAGES.map((p) => (
                  <li key={p.to}>
                    <Link
                      to={p.to}
                      className="group flex items-baseline gap-3 text-[14px] text-annot hover:text-graphite"
                    >
                      <span className="sheet-no group-hover:text-redline">{p.sheetNo}</span>
                      {p.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <p className="annot">Service areas</p>
              <ul className="mt-4 space-y-2.5 text-[14px] text-annot">
                {site.serviceAreas.map((a) => (
                  <li key={a.slug}>{a.name}</li>
                ))}
              </ul>
            </div>
          </div>

          {/* ── Title block ────────────────────────────────────────────────
              Every drawing sheet carries one. Ontario's advertising rules
              require the brokerage's registered name at least as prominently
              as the salesperson's — in a title block of equal cells that's
              satisfied by the structure itself rather than by fine print. */}
          <div className="mt-12 grid grid-cols-1 gap-px border border-medline bg-medline sm:grid-cols-4">
            <Cell label="Project" wide>
              {site.brand.name}
            </Cell>
            <Cell label="Salesperson">
              {site.agent.name}
              <span className="mt-0.5 block font-sans text-[12px] text-annot">{site.agent.title}</span>
            </Cell>
            <Cell label="Brokerage">
              {site.agent.brokerage}
              <span className="mt-0.5 block font-sans text-[12px] text-annot">{site.agent.brokerageCity}</span>
            </Cell>

            <Cell label="Location" wide>
              {site.serviceAreas.map((a) => a.name).join(' · ')}
            </Cell>
            {/* Registration only appears once the real number exists. Issued
                widens to fill the row so the title block keeps its 4-up grid. */}
            {site.agent.registration && <Cell label="Registration">{site.agent.registration}</Cell>}
            <Cell label="Issued" wide={!site.agent.registration}>
              {year}
            </Cell>
          </div>

          <p className="mt-5 text-[12px] leading-relaxed text-annot">{site.legal.regulator}</p>

          {/* RECO/TRESA identification credit — the one place the
              salesperson's registered name appears on the site. */}
          <p className="mt-2 text-[12px] leading-relaxed text-annot">
            Shakir Qureshi, Salesperson · Hometon Inc., Brokerage
          </p>

          <div className="mt-8 flex flex-wrap items-center justify-between gap-x-5 gap-y-2 border-t border-hairline pt-6">
            <span className="font-mono text-[10px] uppercase tracking-wider2 text-annot">
              © {year} {site.brand.legalName}
            </span>
            <Link to="/admin" className="font-mono text-[10px] uppercase tracking-wider2 text-medline hover:text-graphite">
              Brokerage login
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
